import { useCallback, useEffect, useState } from 'react';
import { labCycleAPI, pmaImportAPI } from '../services/api';
import waitForLabCycleAction from './waitForLabCycleAction';

const POLL_MS = 20000;

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function useLabCycle({ extendExpiry = false } = {}) {
  const [watch, setWatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionReport, setActionReport] = useState(null);
  const [actionKey, setActionKey] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [trackedJob, setTrackedJob] = useState(null);
  const [progressError, setProgressError] = useState('');
  const [checkingServers, setCheckingServers] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await labCycleAPI.getWatch();
      setWatch(res.data);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'Could not load the lab user cycle'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(() => load({ silent: true }), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const act = useCallback(
    async (account, action, options = {}) => {
      if (action === 'delete' && trackedJob?.jobId) {
        setProgressError(
          `Delete for ${trackedJob.unixUsername || account.unixUsername} is already job ${trackedJob.jobId}. Check progress again. A new delete was not started.`
        );
        return;
      }
      if (action === 'delete' && options.keepBackup !== true && options.keepBackup !== false) {
        setDeleteTarget(account);
        return;
      }
      const verb = action === 'delete' ? 'Delete' : action === 'enable' ? 'Enable' : 'Disable';
      const confirmed =
        action === 'delete' ||
        window.confirm(
          action === 'enable'
            ? extendExpiry
              ? `Enable ${account.unixUsername}? This unlocks the login, starts VNC, sets the account expiry 45 days from today, and restarts the password age. The password itself is not changed.`
              : `Enable ${account.unixUsername}? This unlocks the login and starts VNC. Account expiry and an expired password are not reset.`
            : `Disable ${account.unixUsername}? This locks the login and stops VNC. The home stays.`
        );
      if (!confirmed) return;
      if (action === 'delete') setDeleteTarget(null);
      setActionKey(`${account.unixUsername}:${action}`);
      setError('');
      setNotice('');
      setProgressError('');
      setActionReport(null);
      let holdJob = false;
      try {
        const res = await labCycleAPI.act(account.unixUsername, action, {
          keepBackup: options.keepBackup === true,
        });
        const jobId = res?.data?.jobId;
        if (res?.status === 202 && jobId) {
          setTrackedJob({ jobId, unixUsername: account.unixUsername, action, status: 'running', step: 'starting', phase: 'precheck', outcome: 'running' });
        }
        const outcome = await waitForLabCycleAction(res, (job) => {
          setTrackedJob({ ...job, unixUsername: account.unixUsername, action });
          if (job?.progressUnavailable) {
            setProgressError('The last status check did not reach the server. Checking the same job again.');
          } else {
            setProgressError('');
          }
        });
        setTrackedJob(null);
        setProgressError('');
        setActionReport(outcome);
        await load({ silent: true });
      } catch (err) {
        if (err.actionReport) {
          setTrackedJob(null);
          setProgressError('');
          setActionReport(err.actionReport);
          await load({ silent: true });
        } else if (err.progressLost) {
          holdJob = err.statusCode !== 401 && err.statusCode !== 404;
          setProgressError(err.message);
          if (!holdJob) setTrackedJob(null);
          else if (err.lastJob) setTrackedJob({ ...err.lastJob, unixUsername: account.unixUsername, action });
        } else {
          setError(errorMessage(err, `${verb} failed for ${account.unixUsername}`));
        }
      } finally {
        if (!holdJob) setActionKey('');
      }
    },
    [load, extendExpiry, trackedJob]
  );

  const resumeProgress = useCallback(async () => {
    if (!trackedJob?.jobId) return;
    const account = { unixUsername: trackedJob.unixUsername };
    const action = trackedJob.action || 'delete';
    setActionKey(`${account.unixUsername}:${action}`);
    setProgressError('');
    setError('');
    let holdJob = true;
    try {
      const outcome = await waitForLabCycleAction({ status: 202, data: { jobId: trackedJob.jobId } }, (job) => {
        setTrackedJob({ ...job, unixUsername: account.unixUsername, action });
      });
      holdJob = false;
      setTrackedJob(null);
      setActionReport(outcome);
      await load({ silent: true });
    } catch (err) {
      if (err.actionReport) {
        holdJob = false;
        setTrackedJob(null);
        setActionReport(err.actionReport);
        await load({ silent: true });
      } else if (err.progressLost) {
        holdJob = err.statusCode !== 401 && err.statusCode !== 404;
        setProgressError(err.message);
        if (!holdJob) setTrackedJob(null);
        else if (err.lastJob) setTrackedJob({ ...err.lastJob, unixUsername: account.unixUsername, action });
      } else {
        holdJob = false;
        setError(errorMessage(err, 'Could not read the server action'));
      }
    } finally {
      if (!holdJob) setActionKey('');
    }
  }, [trackedJob, load]);

  const checkServers = useCallback(async () => {
    setCheckingServers(true);
    setError('');
    try {
      await pmaImportAPI.runLabUserInventory();
      const started = Date.now();
      while (Date.now() - started < 3 * 60 * 1000) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const progress = await pmaImportAPI.getLabUserInventoryProgress();
        const status = progress.data?.status;
        if (status === 'finished' || status === 'failed') {
          if (status === 'failed') {
            setError(progress.data?.error?.message || 'Server check failed');
          }
          break;
        }
      }
      await load({ silent: true });
    } catch (err) {
      setError(errorMessage(err, 'Could not check the lab servers'));
    } finally {
      setCheckingServers(false);
    }
  }, [load]);

  return {
    watch,
    loading,
    error,
    notice,
    actionReport,
    actionKey,
    checkingServers,
    load,
    act,
    deleteTarget,
    cancelDelete: () => setDeleteTarget(null),
    trackedJob,
    progressError,
    resumeProgress,
    checkServers,
    dismissActionReport: () => setActionReport(null),
  };
}
